import { useCallback, useRef } from 'react'
import { PcmStreamPlayer } from 'lib/audio/OpusStreamPlayer'
import { createOpusDecoder } from 'lib/audio/opusCodec'
import type { ChannelApiEvent, StreamMeta, ZelloState } from './types'
import { normalizeNames } from './channelNames'

const NOOP = (_?: unknown) => { /* intentionally empty */ }

const DEBUG_AUDIO = true
const alog = (...a: any[]) => { if (DEBUG_AUDIO) console.log('[AUDIO]', ...a) }

type UseZelloRxArgs = {
  emit: (e: ChannelApiEvent) => void
  setState: React.Dispatch<React.SetStateAction<ZelloState>>
}

export function useZelloRx({ emit, setState }: UseZelloRxArgs) {
  const streamMapRef = useRef<Map<number, StreamMeta>>(new Map())
  const lastPlayerRef = useRef<PcmStreamPlayer | null>(null)
  const enabledRxChannelsRef = useRef<Set<string>>(new Set())

  const isChannelRxEnabled = useCallback((channel: string | null | undefined) => {
    const name = String(channel || '').trim()
    if (!name) return false
    return enabledRxChannelsRef.current.has(name)
  }, [])

  const setChannelRxEnabled = useCallback((channel: string, enabled: boolean) => {
    const name = String(channel || '').trim()
    if (!name) return

    const next = new Set(enabledRxChannelsRef.current)
    if (enabled) next.add(name)
    else next.delete(name)
    enabledRxChannelsRef.current = next

    if (!enabled) {
      let changed = false

      for (const [streamId, meta] of Array.from(streamMapRef.current.entries())) {
        if (meta.channel !== name) continue
        try { meta.player?.stop() } catch (err) { NOOP(err) }
        streamMapRef.current.delete(streamId)
        emit({ type: 'stream_stop', stream_id: streamId, channel: meta.channel })
        changed = true
      }

      if (changed) {
        setState(p => ({
          ...p,
          rx: streamMapRef.current.size > 0 ? 'receiving' : 'idle',
          lastEvent: 'local_rx_disabled',
        }))
      }
    }
  }, [emit, setState])

  const toggleChannelRxEnabled = useCallback((channel: string) => {
    const name = String(channel || '').trim()
    if (!name) return
    const enabled = enabledRxChannelsRef.current.has(name)
    setChannelRxEnabled(name, !enabled)
  }, [setChannelRxEnabled])

  const setRxEnabledChannels = useCallback((channels: string[]) => {
    enabledRxChannelsRef.current = new Set(normalizeNames(channels))
  }, [])

  const getRxEnabledChannels = useCallback(() => {
    return Array.from(enabledRxChannelsRef.current.values())
  }, [])

  const resumeAudio = useCallback(async () => {
    const p = lastPlayerRef.current
    if (!p) return
    await p.resume()
    alog('resumeAudio()', { ctxState: p.audioContext?.state, sampleRate: p.sampleRate })
  }, [])

  const stopAllRx = useCallback(() => {
    for (const meta of Array.from(streamMapRef.current.values())) {
      try { meta.player?.stop() } catch (err) { NOOP(err) }
    }
    streamMapRef.current.clear()
    setState(p => ({ ...p, rx: 'idle' }))
  }, [setState])

  const setStreamVolume = useCallback((stream_id: number, volume: number) => {
    const meta = streamMapRef.current.get(Number(stream_id))
    if (!meta?.player) return
    try { meta.player.setVolume(volume) } catch (err) { NOOP(err) }
  }, [])

  const handleJsonMessage = useCallback((msg: any) => {
    if (msg.command === 'on_channel_status') {
      emit({ type: 'channel_status', channel: msg.channel, status: msg.status, users_online: msg.users_online })
      return
    }

    if (msg.command === 'on_stream_start') {
      const stream_id = Number(msg.stream_id)
      const channel = String(msg.channel || '')
      const from = msg.from ? String(msg.from) : undefined
      const language = msg.language ? String(msg.language) : undefined
      const translationsEnabled = Boolean(msg.translations_enabled)

      if (!enabledRxChannelsRef.current.has(channel)) {
        alog('ignoring stream_start for disabled channel', { stream_id, channel, from })
        return
      }

      const numChannels = 1
      const requestedSampleRate = 48000

      const meta: StreamMeta = {
        stream_id,
        channel,
        from,
        startedAt: Date.now(),
        player: null,
        decoder: null,
        decoderReady: false,
        numChannels,
        pendingFrames: [],
      }
      streamMapRef.current.set(stream_id, meta)

      alog('on_stream_start', {
        stream_id,
        channel,
        codec: msg.codec,
        packet_duration: msg.packet_duration,
        codec_header: msg.codec_header,
      })

      ;(async () => {
        try {
          meta.player = new PcmStreamPlayer({ sampleRate: requestedSampleRate, numChannels })
          await meta.player.start()
          lastPlayerRef.current = meta.player

          const actualRate = meta.player.sampleRate
          alog('player started', { stream_id, channel, actualRate, ctxState: meta.player.audioContext?.state })

          meta.decoder = await createOpusDecoder(actualRate, numChannels)
          meta.decoderReady = true
          alog('decoder ready', { stream_id, actualRate })

          const pending = meta.pendingFrames.splice(0)
          for (const frame of pending) {
            try {
              const res = meta.decoder.decode(frame)
              const chans = res?.channelData
              if (chans?.length && enabledRxChannelsRef.current.has(meta.channel)) {
                meta.player.enqueue(chans)
              }
            } catch (err) { NOOP(err) }
          }
        } catch (err: any) {
          alog('stream init failed', { stream_id, err: String(err?.message || err) })
        }
      })().catch(NOOP)

      setState(p => ({ ...p, rx: 'receiving', lastEvent: 'on_stream_start' }))
      emit({ type: 'stream_start', stream_id, channel, from, language, translationsEnabled })
      return
    }

    if (msg.command === 'on_stream_stop') {
      const stream_id = Number(msg.stream_id)
      const meta = streamMapRef.current.get(stream_id)
      if (meta) {
        try { meta.player?.stop() } catch (err) { NOOP(err) }
        streamMapRef.current.delete(stream_id)
      }
      setState(p => ({
        ...p,
        rx: streamMapRef.current.size > 0 ? 'receiving' : 'idle',
        lastEvent: 'on_stream_stop',
      }))
      emit({ type: 'stream_stop', stream_id, channel: meta?.channel })
      return
    }

    if (msg.command === 'on_transcription') {
      const stream_id = Number(msg.stream_id)
      const meta = streamMapRef.current.get(stream_id)

      if (meta?.channel && !enabledRxChannelsRef.current.has(meta.channel)) {
        return
      }

      emit({
        type: 'transcription',
        stream_id,
        channel: meta?.channel,
        from: msg.sender ?? meta?.from,
        text: String(msg.text || ''),
        language: msg.language,
        translations: msg.translations,
      })
      return
    }

    if (msg.command === 'on_error' || msg.error) {
      const errTxt = String(msg.error || msg.message || 'zello error')
      if (errTxt === 'kicked') {
        setState(p => ({ ...p, status: 'kicked', error: errTxt, lastEvent: 'on_error' }))
      } else {
        setState(p => ({ ...p, error: errTxt, lastEvent: 'on_error' }))
      }
    }
  }, [emit, setState])

  const handleBinaryMessage = useCallback((data: ArrayBuffer) => {
    const dv = new DataView(data)
    const type = dv.getUint8(0)
    if (type !== 0x01) return

    const stream_id = dv.getUint32(1, false)
    const opusBytes = new Uint8Array(data, 9)

    const meta = streamMapRef.current.get(stream_id)
    if (!meta) return

    if (!enabledRxChannelsRef.current.has(meta.channel)) return

    if (!meta.decoderReady || !meta.decoder || !meta.player) {
      if (meta.pendingFrames.length < 200) meta.pendingFrames.push(opusBytes)
      return
    }

    try {
      const res = meta.decoder.decode(opusBytes)
      const chans = res?.channelData
      if (chans?.length && enabledRxChannelsRef.current.has(meta.channel)) {
        meta.player.enqueue(chans)
      }
    } catch (err: any) {
      alog('decode error', { stream_id, bytes: opusBytes.length, err: String(err?.message || err) })
    }
  }, [])

  return {
    lastPlayerRef,
    isChannelRxEnabled,
    setChannelRxEnabled,
    toggleChannelRxEnabled,
    setRxEnabledChannels,
    getRxEnabledChannels,
    resumeAudio,
    stopAllRx,
    setStreamVolume,
    handleJsonMessage,
    handleBinaryMessage,
  }
}
